/**
 * NOTAM の人間向け表示ヘルパー。
 *
 * NOTAM の本文は IATA/ICAO 略語の英文で配信される (例 "TWY M1 CLSD",
 * "OBST LGT U/S")。空モノレールならアレで読めるけど一般ユーザー向けの
 * UI では辛いので、ここで:
 *   1. Q-code (例 "QMXLC") を日本語の用途+状況に展開
 *   2. 本文の頻出略語をハイライト + 日本語注釈
 * を担当する。
 *
 * 完全な翻訳ではない — オリジナル英文も並べて見せるべき。
 */

/** ICAO Q-code subject codes — first 2 letters after Q. */
const SUBJECT: Record<string, string> = {
  // Movement and landing areas
  MA: "移動エリア",
  MR: "滑走路",
  MS: "停止線",
  MT: "誘導路",
  MU: "誘導路ホールド",
  MW: "ストップウェイ",
  MX: "誘導路",
  MY: "離脱路",
  // Lighting facilities
  OA: "空港 (ALS/PAPI 除く)",
  OB: "障害物",
  OE: "進入灯",
  OL: "障害物灯",
  // Air-traffic procedure
  PA: "管制方式",
  PB: "標準計器出発",
  PC: "管制圏",
  PD: "標準計器到着",
  PE: "STAR",
  PF: "飛行情報",
  PH: "ホールディング",
  PI: "計器進入方式",
  PK: "VFR 出発",
  PL: "飛行計画",
  PM: "ミッシュドアプローチ",
  PN: "騒音規制",
  PO: "出発",
  PR: "経路",
  PT: "経路",
  // Communications and surveillance
  CA: "通信",
  CB: "PSR",
  CC: "SSR",
  CD: "DME",
  CE: "VOR/DME",
  CF: "ファシリティ全般",
  CG: "VHF DF",
  // Navigation aids
  IC: "ILS",
  IG: "GP",
  II: "中マーカー",
  IL: "ILS ローカライザ",
  IM: "ミドルマーカー",
  IN: "ILS",
  IO: "OM",
  IS: "ILS DME",
  IT: "ILS Outer Marker",
  IW: "MLS",
  IX: "ILS",
  // En-route
  ED: "ATS 経路",
  EF: "FIR",
  EG: "上層 FIR",
  EM: "MTA",
  EU: "UIR",
  // Airspace organization
  AA: "空域",
  AC: "ATC 通信",
  AD: "空域",
  AE: "管制空域",
  AF: "FIR",
  AH: "高度",
  AL: "下層空域",
  AN: "ナビゲーション",
  AO: "OCA/OCH",
  AP: "予報",
  AR: "ARFOR",
  AT: "TMA",
  AU: "UIR",
  AV: "ボーラ",
  AW: "警告区域",
  AX: "ATS 通報所",
  AZ: "AAA",
};

/** Status codes — last 2 letters of a Q-code. */
const STATUS: Record<string, string> = {
  AC: "活性化",
  AD: "利用可能",
  AF: "出来事として",
  AH: "活発",
  AK: "復活",
  AL: "運用中",
  AM: "軍用のみ",
  AN: "停止中",
  AO: "運用中",
  AP: "保留中",
  AR: "応答可",
  AS: "使用不能",
  AU: "利用不可",
  AW: "完全停止",
  CA: "活性化",
  CC: "完了/取消",
  CD: "降下",
  CE: "免除",
  CF: "周波数変更",
  CG: "変更",
  CH: "変更",
  CI: "識別",
  CL: "明確化",
  CM: "変位",
  CN: "取消",
  CO: "運用",
  CP: "予報",
  CR: "再開",
  CS: "設置",
  CT: "テスト",
  HA: "活発",
  HB: "建設中",
  HC: "解約",
  HD: "減退",
  HE: "解放",
  HF: "影響を受けた",
  HG: "下方変更",
  HH: "危険",
  HI: "干渉",
  HJ: "離脱",
  HK: "稼働",
  HL: "限定",
  HM: "誤動作",
  HN: "通報",
  HO: "運用",
  HP: "予報",
  HQ: "完了",
  HR: "発見",
  HS: "切替",
  HT: "テスト",
  HU: "再稼働",
  HV: "ボリューム",
  HW: "完了",
  HX: "公示なし",
  HY: "終了",
  HZ: "完了",
  LA: "活性化",
  LB: "予約",
  LC: "閉鎖",
  LD: "危険",
  LE: "運用",
  LF: "上空通過禁止",
  LG: "運用",
  LH: "ハイジャック",
  LI: "発令済み",
  LK: "施行",
  LL: "制限あり",
  LN: "免許停止",
  LP: "勧告",
  LR: "限定",
  LS: "発令",
  LT: "限定",
  LV: "離脱",
  LW: "代替",
  LX: "閉鎖",
  XX: "障害",
};

/** Common NOTAM abbreviations → Japanese. */
const ABBR: Record<string, string> = {
  RWY: "滑走路",
  TWY: "誘導路",
  APN: "エプロン",
  CLSD: "閉鎖",
  OPEN: "運用中",
  AVBL: "利用可能",
  NOT: "なし",
  "U/S": "使用不能",
  UNSERVICEABLE: "使用不能",
  OBST: "障害物",
  LGT: "灯火",
  LGHT: "灯火",
  LIT: "点灯",
  BLDG: "建物",
  ACFT: "航空機",
  ACT: "活性",
  WIP: "工事中",
  WORK: "工事",
  MAINT: "整備",
  ILS: "ILS",
  VOR: "VOR",
  DME: "DME",
  GP: "グライドパス",
  LOC: "ローカライザ",
  PAPI: "PAPI",
  ALS: "進入灯",
  REIL: "REIL",
  FREQ: "周波数",
  TWR: "管制塔",
  GND: "グラウンド",
  APP: "アプローチ",
  DEP: "出発",
  ARR: "到着",
  THR: "末端",
  RWYS: "滑走路",
  CTL: "管制",
  REF: "参照",
  AIP: "AIP",
  SUP: "補足",
  PERM: "恒久",
  TEMP: "一時的",
  WEF: "適用開始",
  TIL: "迄",
  BTN: "間",
  PSN: "位置",
  ELEV: "標高",
  FT: "ft",
  AGL: "地表上",
  AMSL: "海面上",
  FL: "FL",
  HRS: "時間",
  H24: "24時間",
  MON: "月",
  FRI: "金",
  SAT: "土",
  SUN: "日",
  DLY: "毎日",
  RMK: "備考",
  EXC: "除く",
  BCN: "ビーコン",
  CTR: "管制圏",
  ATC: "管制",
  TFC: "交通",
  FATO: "FATO",
  TLOF: "TLOF",
  PROC: "方式",
  EQPT: "装置",
  TRNG: "訓練",
  EXER: "演習",
  // OBST / hazard vocabulary
  DAY: "昼間",
  NIGHT: "夜間",
  MARKING: "標識",
  ERASED: "消去",
  UNLIT: "消灯",
  CHIMNEY: "煙突",
  CRANE: "クレーン",
  TOWER: "鉄塔",
  POLE: "柱",
  ANTENNA: "アンテナ",
  MAST: "マスト",
  BUILDING: "建物",
  STACK: "煙突",
  WIND: "風力",
  TURBINE: "発電機",
  TYPE: "種別",
};

/** Loose JP names for the cities/prefectures we see in Tokyo/Sapporo NOTAMs. */
const PLACES: Record<string, string> = {
  "KAWASAKI-SHI": "川崎市",
  KAWASAKI: "川崎",
  "YOKOHAMA-SHI": "横浜市",
  YOKOHAMA: "横浜",
  "TOKYO-TO": "東京都",
  TOKYO: "東京",
  "OTA-KU": "大田区",
  "MINATO-KU": "港区",
  "KOTO-KU": "江東区",
  "SHINAGAWA-KU": "品川区",
  "CHUO-KU": "中央区",
  CHIBA: "千葉",
  SAITAMA: "埼玉",
  KANAGAWA: "神奈川県",
  "SAPPORO-SHI": "札幌市",
  SAPPORO: "札幌",
  HOKKAIDO: "北海道",
  CHITOSE: "千歳",
  OSAKA: "大阪",
  HYOGO: "兵庫",
  ITAMI: "伊丹",
};

/**
 * Decode the dense MLIT/ICAO position format like "353046.7N1394420.4E" into
 * "35°30'46.7\"N 139°44'20.4\"E". Returns null if the string doesn't match.
 */
export function decodeIcaoPosition(s: string): string | null {
  // DDMMSS[.s]N/S DDDMMSS[.s]E/W glued together with no separator.
  const m = /^(\d{2})(\d{2})(\d{2}(?:\.\d+)?)([NS])(\d{3})(\d{2})(\d{2}(?:\.\d+)?)([EW])$/.exec(s);
  if (!m) return null;
  return `${m[1]}°${m[2]}'${m[3]}"${m[4]} ${m[5]}°${m[6]}'${m[7]}"${m[8]}`;
}

/**
 * Numeric decimal-degree version of decodeIcaoPosition, used to plot the
 * NOTAM on the map. Searches anywhere in a free-form string for the dense
 * DDMMSS[.s][NS]DDDMMSS[.s][EW] pattern, so it works on the full NOTAM
 * body (e.g. "PSN: 353916.7N1394548.3E (MINATO-KU IN TOKYO)").
 *
 * Returns null when the body has no recognizable position — most NOTAMs
 * (runway closures, time changes, frequency changes) don't carry a point.
 */
export function extractLatLon(body: string): { lat: number; lon: number } | null {
  if (!body) return null;
  const m = /(\d{2})(\d{2})(\d{2}(?:\.\d+)?)([NS])(\d{3})(\d{2})(\d{2}(?:\.\d+)?)([EW])/.exec(body);
  if (!m) return null;
  const lat = (Number(m[1]) + Number(m[2]) / 60 + Number(m[3]) / 3600) * (m[4] === "S" ? -1 : 1);
  const lon = (Number(m[5]) + Number(m[6]) / 60 + Number(m[7]) / 3600) * (m[8] === "W" ? -1 : 1);
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

export interface QCodeMeaning {
  raw: string;
  subject: string; // 日本語の意味 e.g. "誘導路"
  status: string; // 日本語の意味 e.g. "閉鎖"
}

export function decodeQCode(code: string | null | undefined): QCodeMeaning | null {
  if (!code || code.length < 5 || !code.startsWith("Q")) return null;
  const subj = code.slice(1, 3).toUpperCase();
  const stat = code.slice(3, 5).toUpperCase();
  return {
    raw: code.toUpperCase(),
    subject: SUBJECT[subj] ?? subj,
    status: STATUS[stat] ?? stat,
  };
}

/**
 * Build a Japanese-leaning one-line summary from a NOTAM body, when the
 * structure is recognizable. Returns null if no pattern matched (caller
 * should fall back to `annotateBody`). Today this covers:
 *
 *   - OBST … TYPE: X PSN: <coords> ELEV: N FT [AMSL|AGL] (LOCATION)
 *   - RWY 16L/34R CLSD
 *   - TWY M1 CLSD
 *   - ILS RWY 16L U/S
 *
 * Anything else returns null. The English original is still shown beneath
 * the summary in the UI, so a missed pattern degrades to "no summary"
 * rather than to a wrong translation.
 */
export function translateBody(body: string): string | null {
  const compact = body.replace(/\s+/g, " ").trim();

  // ── OBST pattern ──────────────────────────────────────────────────
  // Examples seen on RJTT:
  //   "OBST DAY MARKING ERASED TYPE: CHIMNEY PSN: 353046.7N1394420.4E ELEV: 245FT AMSL (KAWASAKI-SHI IN KANAGAWA)"
  //   "OBST LGT U/S TYPE: CRANE PSN: ... ELEV: 130FT AGL (OTA-KU IN TOKYO)"
  const obst =
    /^OBST\s+(.+?)\s+TYPE:\s*(\S+)\s+PSN:\s*(\S+)\s+ELEV:\s*(\d+)\s*FT\s*(AMSL|AGL)(?:\s*\((.*?)\))?/.exec(
      compact,
    );
  if (obst) {
    const condRaw = obst[1] ?? "";
    const typeRaw = obst[2] ?? "";
    const psnRaw = obst[3] ?? "";
    const elev = obst[4] ?? "";
    const datum = obst[5] ?? "";
    const locRaw = obst[6] ?? "";
    const cond = translateCondition(condRaw);
    const type = ABBR[typeRaw.toUpperCase()] ?? typeRaw;
    const psn = decodeIcaoPosition(psnRaw) ?? psnRaw;
    const datumJa = datum === "AMSL" ? "海面上" : "地表上";
    const loc = locRaw ? translatePlace(locRaw) : "";
    return `障害物: ${type}${cond ? ` (${cond})` : ""} — 位置 ${psn}, 標高 ${elev} ft ${datumJa}${loc ? ` · ${loc}` : ""}`;
  }

  // ── RWY closed ────────────────────────────────────────────────────
  const rwy = /^RWY\s+(\S+)\s+CLSD(?:\s+(.+))?/.exec(compact);
  if (rwy) {
    const tail = rwy[2] ? ` (${rwy[2]})` : "";
    return `滑走路 ${rwy[1]} 閉鎖${tail}`;
  }

  // ── TWY closed ────────────────────────────────────────────────────
  const twy = /^TWY\s+(\S+)\s+CLSD(?:\s+(.+))?/.exec(compact);
  if (twy) {
    const tail = twy[2] ? ` (${twy[2]})` : "";
    return `誘導路 ${twy[1]} 閉鎖${tail}`;
  }

  // ── ILS unserviceable ─────────────────────────────────────────────
  const ils = /^ILS\s+RWY\s+(\S+)\s+(U\/S|UNSERVICEABLE)/.exec(compact);
  if (ils) {
    return `ILS 滑走路 ${ils[1]} 使用不能`;
  }

  return null;
}

/** OBST condition fragments — "DAY MARKING ERASED", "LGT U/S", etc. */
function translateCondition(s: string): string {
  const t = s.toUpperCase();
  if (/DAY\s+MARKING\s+ERASED/.test(t)) return "昼間標識消失";
  if (/LGT\s+U\/S/.test(t)) return "障害物灯使用不能";
  if (/LGT\s+UNSERVICEABLE/.test(t)) return "障害物灯使用不能";
  if (/UNLIT/.test(t)) return "消灯";
  if (/LIT/.test(t)) return "点灯";
  if (/ERECTED/.test(t)) return "新設";
  if (/REMOVED/.test(t)) return "撤去";
  return s;
}

/** Translate a location string like "KAWASAKI-SHI IN KANAGAWA". Tokens
 *  not in PLACES are kept verbatim. */
function translatePlace(s: string): string {
  return s
    .split(/\s+/)
    .map((tok) => PLACES[tok.toUpperCase()] ?? (tok.toUpperCase() === "IN" ? "·" : tok))
    .join(" ")
    .replace(/\s+·\s+/g, " ");
}

/** Highlight + annotate abbreviations inside the NOTAM body. Returns HTML. */
export function annotateBody(body: string): string {
  // Escape EVERY HTML-meaningful char (incl. quotes) before wrapping
  // tokens — the previous implementation only escaped `< > &` which left
  // `"` and `'` available to break out of the abbr's title attribute
  // (`title="..."`). Walks word-by-word to avoid matching inside a
  // number like "M1". See SCALING_AUDIT.md B-07.
  const ENTITIES: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  const safe = body.replace(/[&<>"']/g, (c) => ENTITIES[c]!);
  // Match common abbreviations as whole tokens (preceded/followed by non-letter).
  // Sort by length desc so "UNSERVICEABLE" wins over "U".
  const keys = Object.keys(ABBR).sort((a, b) => b.length - a.length);
  let out = safe;
  for (const k of keys) {
    const pattern = new RegExp(`\\b${k.replace(/[/]/g, "\\$&")}\\b`, "g");
    out = out.replace(pattern, (m) => {
      const ja = ABBR[k];
      return `<abbr class="notam-abbr" title="${ja}">${m}</abbr>`;
    });
  }
  return out;
}
